// @flow

import * as React from 'react';
import styled from 'styled-components';
import { InputOption } from '../../internal/internal-typography';
import Theme from './theme';
import { Grid } from '../../layouts/grid';
/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
import type { ReactComponentStyled } from 'styled-components';

/*
  Adapted from this blog post:
  https://css-tricks.com/custom-styling-form-inputs-with-modern-css-features/

  One commenter converted the sass in the post into react and styled components:
  https://codesandbox.io/s/custom-css-only-form-inputs-react-hofx5?file=/src/Checkbox.tsx
 */

type CheckboxProps = StandaloneCheckboxProps & {
  /** Every checkbox should have an associated label for accessibility */
  label: string,
  value: string,
};

type StandaloneCheckboxProps = {
  /** prevent interacting with the checkbox */
  disabled?: boolean,
  /** Current state */
  checked: boolean,
  /**
   * On change callback
   *
   * @param checked the new state of the checkbox (a shortcut for event.currentTarget.checked)
   * @param e the full event. */
  onChange: (checked: boolean, e?: SyntheticEvent<HTMLInputElement>) => void,
};

const Checkbox = (props: CheckboxProps) => {
  const { disabled, label, ...rest } = props;

  return (
    <StyledLabel disabled={disabled}>
      <Grid container spacing={2}>
        <Grid item>
          <StandaloneCheckbox disabled={disabled} {...rest} />
        </Grid>
        <Grid item xs>
          {label}
        </Grid>
      </Grid>
    </StyledLabel>
  );
};
Checkbox.displayName = 'Checkbox';

/**
 * This checkbox is not documented in the storybook because it should not be
 * used by consumers directly. Instead it should be used as part of other
 * seedling components that take into account accessibility.
 */
export const StandaloneCheckbox = (props: StandaloneCheckboxProps) => {
  const { onChange, ...rest } = props;

  let wrappingOnChange;

  if (onChange) {
    wrappingOnChange = (e) => {
      onChange(e.currentTarget.checked, e);
    };
  }

  return (
    <CheckboxWrapper>
      <input type="checkbox" {...rest} onChange={wrappingOnChange} />
      <VisualCheckbox />
    </CheckboxWrapper>
  );
};
StandaloneCheckbox.defaultProps = {
  disabled: false,
};
StandaloneCheckbox.displayName = 'StandaloneCheckbox';

const VisualCheckbox = () => {
  return (
    <span>
      <Checkmark />
    </span>
  );
};

const StyledLabel: ReactComponentStyled<{}> = styled(InputOption)`
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `
  cursor: not-allowed;
  color: ${Theme.disabled.labelColor};
  `};
`;

const Checkmark = () => {
  return (
    <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.195.437a.965.965 0 0 1 1.39-.24c.446.336.549.99.228 1.46L4.426 9.564a.966.966 0 0 1-1.534.108L.272 6.748a1.086 1.086 0 0 1 .04-1.481.962.962 0 0 1 1.409.043L3.512 7.31 8.195.437Z" />
    </svg>
  );
};

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const CheckboxWrapper = styled.span`
  input {
    // Hide the browser default checkbox in a purely visual way (will still be recognized by screen readers)
    appearance: none;
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  span {
    background-color: ${Theme.default.backgroundColor};
    display: inline-block;
    position: relative;
    height: 1.125rem;
    width: 1.125rem;
    border: 1px solid ${Theme.default.inputColor};
    border-radius: 5px;
    box-sizing: border-box;
    top: 0.1875rem;
    left: 0;

    svg {
      fill: ${Theme.default.inputColor};
      opacity: 0;
      position: absolute;
      top: 0.1875rem;
      left: 0.1875rem;
    }
  }

  input:checked + span > svg {
    opacity: 1;
  }

  input:focus + span {
    box-shadow: 0px 0px 5px 0px ${Theme.default.inputColor}99;
  }

  input:hover + span {
    border-color: ${Theme.hover.inputColor};

    > svg path {
      fill: ${Theme.hover.inputColor};
    }
  }

  input:active + span {
    border-color: ${Theme.active.inputColor};

    > svg path {
      fill: ${Theme.active.inputColor};
    }
  }

  input:disabled + span {
    border-color: ${Theme.disabled.inputColor};
    cursor: not-allowed;

    > svg path {
      fill: ${Theme.disabled.inputColor};
    }
  }
`;

export default Checkbox;
